import React, { useState, useEffect } from 'react'
import NiceAvatar, { genConfig } from 'react-nice-avatar'

interface AvatarEditorProps {
  isOpen: boolean
  onClose: () => void
  onSave: (customization: any, seed: string) => void
  initialSeed?: string
  initialCustomization?: any
  title?: string
  userName?: string
}

const AvatarEditor: React.FC<AvatarEditorProps> = ({
  isOpen,
  onClose,
  onSave,
  initialSeed = '',
  initialCustomization = {},
  title = 'Personalizar Avatar',
  userName = 'Usuario'
}) => {
  // Estado interno del editor
  const [avatarSeed, setAvatarSeed] = useState(initialSeed)
  const [avatarConfig, setAvatarConfig] = useState(() => {
    if (Object.keys(initialCustomization).length > 0) {
      return initialCustomization
    }
    return genConfig(initialSeed || undefined)
  })

  // Sincronizar con props cuando cambian
  useEffect(() => {
    setAvatarSeed(initialSeed)
    if (Object.keys(initialCustomization).length > 0) {
      setAvatarConfig(initialCustomization)
    } else {
      setAvatarConfig(genConfig(initialSeed || undefined))
    }
  }, [initialSeed, initialCustomization])

  // Manejar guardado
  const handleSave = () => {
    onSave(avatarConfig, avatarSeed)
    onClose()
  }

  // Manejar cancelar
  const handleCancel = () => {
    // Revertir cambios
    setAvatarSeed(initialSeed)
    if (Object.keys(initialCustomization).length > 0) {
      setAvatarConfig(initialCustomization)
    } else {
      setAvatarConfig(genConfig(initialSeed || undefined))
    }
    onClose()
  }

  // Generar avatar aleatorio
  const generateRandomAvatar = () => {
    const newSeed = Math.random().toString(36).substring(2, 15)
    const newConfig = genConfig(newSeed)
    setAvatarSeed(newSeed)
    setAvatarConfig(newConfig)
  }

  // Generar avatar por nombre
  const generateFromName = () => {
    if (userName) {
      const nameConfig = genConfig(userName)
      setAvatarSeed(userName)
      setAvatarConfig(nameConfig)
    }
  }

  // Opciones de personalización
  const customizationOptions = {
    faceColor: [
      { value: '#F9C9B6', label: 'Muy Claro' },
      { value: '#F1C27D', label: 'Claro' },
      { value: '#E0AC69', label: 'Medio Claro' },
      { value: '#C68642', label: 'Medio' },
      { value: '#AC6651', label: 'Medio Oscuro' },
      { value: '#8D5524', label: 'Oscuro' },
      { value: '#77311B', label: 'Muy Oscuro' }
    ],
    earSize: [
      { value: 'small', label: 'Pequeñas' },
      { value: 'big', label: 'Grandes' }
    ],
    hairStyle: [
      { value: 'normal', label: 'Normal' },
      { value: 'thick', label: 'Grueso' },
      { value: 'mohawk', label: 'Cresta' },
      { value: 'womanLong', label: 'Largo' },
      { value: 'womanShort', label: 'Corto' }
    ],
    hairColor: [
      { value: '#000000', label: 'Negro' },
      { value: '#2C1B18', label: 'Castaño Oscuro' },
      { value: '#77311B', label: 'Castaño' },
      { value: '#A0522D', label: 'Castaño Claro' },
      { value: '#DEB887', label: 'Rubio Oscuro' },
      { value: '#F5DEB3', label: 'Rubio' },
      { value: '#FFDC00', label: 'Rubio Claro' },
      { value: '#CD853F', label: 'Auburn' },
      { value: '#D2691E', label: 'Pelirrojo' },
      { value: '#B22222', label: 'Rojo Intenso' },
      { value: '#C0C0C0', label: 'Plateado' },
      { value: '#808080', label: 'Gris' },
      { value: '#FFFFFF', label: 'Blanco' },
      { value: '#4169E1', label: 'Azul' },
      { value: '#8A2BE2', label: 'Violeta' },
      { value: '#FF1493', label: 'Rosa' },
      { value: '#32CD32', label: 'Verde' }
    ],
    eyeStyle: [
      { value: 'circle', label: 'Círculo' },
      { value: 'oval', label: 'Oval' },
      { value: 'smile', label: 'Sonrisa' }
    ],
    noseStyle: [
      { value: 'short', label: 'Corta' },
      { value: 'long', label: 'Larga' },
      { value: 'round', label: 'Redonda' }
    ],
    mouthStyle: [
      { value: 'laugh', label: 'Risa' },
      { value: 'smile', label: 'Sonrisa' },
      { value: 'peace', label: 'Tranquilo' }
    ],
    shirtStyle: [
      { value: 'hoody', label: 'Sudadera' },
      { value: 'short', label: 'Camiseta' },
      { value: 'polo', label: 'Polo' }
    ],
    hatStyle: [
      { value: 'none', label: 'Sin sombrero' },
      { value: 'beanie', label: 'Gorro' },
      { value: 'turban', label: 'Turbante' }
    ],
    glassesStyle: [
      { value: 'none', label: 'Sin lentes' },
      { value: 'round', label: 'Redondos' },
      { value: 'square', label: 'Cuadrados' }
    ],
    backgroundOptions: [
      // Colores sólidos
      { value: '#87CEEB', gradient: false, label: 'Azul Cielo' },
      { value: '#98FB98', gradient: false, label: 'Verde Claro' },
      { value: '#FFB6C1', gradient: false, label: 'Rosa Claro' },
      { value: '#DDA0DD', gradient: false, label: 'Lavanda' },
      { value: '#F0E68C', gradient: false, label: 'Amarillo Suave' },
      { value: '#FFA07A', gradient: false, label: 'Salmón' },
      { value: '#B0C4DE', gradient: false, label: 'Azul Acero' },
      { value: '#FFFFFF', gradient: false, label: 'Blanco' },
      { value: '#F5F5F5', gradient: false, label: 'Gris Claro' },
      
      // Degradados
      { value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', gradient: true, label: 'Violeta Azul' },
      { value: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', gradient: true, label: 'Rosa Fucsia' },
      { value: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', gradient: true, label: 'Azul Océano' },
      { value: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', gradient: true, label: 'Verde Menta' },
      { value: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', gradient: true, label: 'Atardecer' },
      { value: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', gradient: true, label: 'Pastel Suave' },
      { value: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', gradient: true, label: 'Durazno' },
      { value: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)', gradient: true, label: 'Rosa Dulce' },
    ]
  }

  // Actualizar configuración
  const updateConfig = (key: string, value: any) => {
    setAvatarConfig((prev: any) => ({
      ...prev,
      [key]: value
    }))
  }

  // Actualizar fondo
  const updateBackground = (bgOption: any) => {
    setAvatarConfig((prev: any) => ({
      ...prev,
      bgColor: bgOption.value,
      isGradient: bgOption.gradient
    }))
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {title}
            </h3>
            <button
              onClick={handleCancel}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Avatar Preview - Fixed Top */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-center">
            <div className="relative">
              <NiceAvatar
                style={{ width: '120px', height: '120px' }}
                {...avatarConfig}
              />
              <button
                onClick={generateRandomAvatar}
                className="absolute -top-2 -right-2 bg-purple-500 hover:bg-purple-600 text-white p-1.5 rounded-full shadow-lg transition-all duration-200 transform hover:scale-110"
                title="Generar aleatorio"
              >
                🎲
              </button>
            </div>
          </div>
        </div>

        {/* Options - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            {/* Generación Rápida */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Generación Rápida
              </h4>
              <div className="flex justify-center gap-3">
                <button
                  onClick={generateRandomAvatar}
                  className="btn btn-secondary px-4 py-2"
                >
                  🎲 Aleatorio
                </button>
                <button
                  onClick={generateFromName}
                  className="btn btn-secondary px-4 py-2"
                >
                  👤 Nombre
                </button>
              </div>
            </div>

            {/* Customization Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Color de piel */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Color de piel
                </label>
                <div className="flex gap-2 flex-wrap">
                  {customizationOptions.faceColor.map(option => (
                    <button
                      key={option.value}
                      onClick={() => updateConfig('faceColor', option.value)}
                      className={`w-8 h-8 rounded-full border-2 ${
                        avatarConfig.faceColor === option.value
                          ? 'border-primary ring-2 ring-primary/30'
                          : 'border-gray-300 dark:border-gray-600 hover:border-primary'
                      }`}
                      style={{ backgroundColor: option.value }}
                      title={option.label}
                    />
                  ))}
                </div>
              </div>

              {/* Estilo de cabello */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Estilo de cabello
                </label>
                <select
                  value={avatarConfig.hairStyle || 'normal'}
                  onChange={(e) => updateConfig('hairStyle', e.target.value)}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                >
                  {customizationOptions.hairStyle.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Color de cabello */}
              <div className="col-span-full">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Color de cabello
                </label>
                <div className="grid grid-cols-8 sm:grid-cols-12 lg:grid-cols-17 gap-2">
                  {customizationOptions.hairColor.map(option => {
                    const isSelected = avatarConfig.hairColor === option.value
                    const isWhite = option.value === '#FFFFFF'
                    
                    return (
                      <button
                        key={option.value}
                        onClick={() => updateConfig('hairColor', option.value)}
                        className={`w-8 h-8 rounded-full border-2 ${
                          isSelected
                            ? 'border-primary ring-2 ring-primary/30'
                            : isWhite 
                              ? 'border-gray-400 dark:border-gray-500 hover:border-primary'
                              : 'border-gray-300 dark:border-gray-600 hover:border-primary'
                        }`}
                        style={{ backgroundColor: option.value }}
                        title={option.label}
                      />
                    )
                  })}
                </div>
              </div>

              {/* Tamaño de orejas */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tamaño de orejas
                </label>
                <div className="flex gap-2">
                  {customizationOptions.earSize.map(option => (
                    <button
                      key={option.value}
                      onClick={() => updateConfig('earSize', option.value)}
                      className={`flex-1 p-2 rounded-lg border text-sm ${
                        avatarConfig.earSize === option.value
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:border-primary'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Estilo de ojos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Estilo de ojos
                </label>
                <select
                  value={avatarConfig.eyeStyle || 'circle'}
                  onChange={(e) => updateConfig('eyeStyle', e.target.value)}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                >
                  {customizationOptions.eyeStyle.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Estilo de nariz */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Estilo de nariz
                </label>
                <select
                  value={avatarConfig.noseStyle || 'short'}
                  onChange={(e) => updateConfig('noseStyle', e.target.value)}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                >
                  {customizationOptions.noseStyle.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Estilo de boca */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Estilo de boca
                </label>
                <select
                  value={avatarConfig.mouthStyle || 'smile'}
                  onChange={(e) => updateConfig('mouthStyle', e.target.value)}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                >
                  {customizationOptions.mouthStyle.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Estilo de camisa */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Estilo de camisa
                </label>
                <select
                  value={avatarConfig.shirtStyle || 'hoody'}
                  onChange={(e) => updateConfig('shirtStyle', e.target.value)}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                >
                  {customizationOptions.shirtStyle.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Sombrero */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Sombrero
                </label>
                <select
                  value={avatarConfig.hatStyle || 'none'}
                  onChange={(e) => updateConfig('hatStyle', e.target.value)}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                >
                  {customizationOptions.hatStyle.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Lentes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Lentes
                </label>
                <select
                  value={avatarConfig.glassesStyle || 'none'}
                  onChange={(e) => updateConfig('glassesStyle', e.target.value)}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                >
                  {customizationOptions.glassesStyle.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Color de fondo */}
              <div className="col-span-full">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Color de fondo
                </label>
                <div className="grid grid-cols-8 sm:grid-cols-12 lg:grid-cols-17 gap-1">
                  {customizationOptions.backgroundOptions.map((option, index) => {
                    const isSelected = avatarConfig.bgColor === option.value && avatarConfig.isGradient === option.gradient
                    
                    return (
                      <button
                        key={index}
                        onClick={() => updateBackground(option)}
                        className={`w-8 h-8 rounded-lg border-2 ${
                          isSelected
                            ? 'border-primary ring-2 ring-primary/30'
                            : option.value === '#FFFFFF' || option.value === '#F5F5F5'
                              ? 'border-gray-400 dark:border-gray-500 hover:border-primary'
                              : 'border-gray-300 dark:border-gray-600 hover:border-primary'
                        }`}
                        style={{ 
                          background: option.gradient ? option.value : option.value
                        }}
                        title={option.label}
                      />
                    )
                  })}
                </div>
              </div>
            </div>
          
          {/* Seed Input */}
            <div className="mt-8 max-w-md mx-auto">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Semilla Personalizada
            </label>
              <input
                type="text"
                value={avatarSeed}
                onChange={(e) => {
                  const newSeed = e.target.value
                  setAvatarSeed(newSeed)
                  setAvatarConfig(genConfig(newSeed || undefined))
                }}
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                placeholder="Ingresa cualquier texto para personalizar"
              />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Cada texto genera un avatar único
            </p>
          </div>

          {/* Action Buttons */}
            <div className="flex justify-center space-x-4 mt-8">
            <button
              onClick={handleCancel}
              className="px-6 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              className="btn btn-primary px-6 py-2"
            >
              Guardar Avatar
            </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AvatarEditor 